import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { 
  BarChart3, 
  Users, 
  FileText, 
  Image, 
  Mail, 
  Star, 
  TrendingUp, 
  Activity,
  Calendar,
  Globe
} from 'lucide-react';

const Dashboard = () => {
  const { adminAPI } = useAdmin();
  const [stats, setStats] = useState({
    contacts: 0,
    services: 0,
    projects: 0,
    testimonials: 0,
    media: 0,
    views: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load stats
      const [
        contactsRes,
        servicesRes,
        projectsRes,
        testimonialsRes,
        mediaRes
      ] = await Promise.all([
        adminAPI.getContactSubmissions(),
        adminAPI.getServices(),
        adminAPI.getProjects(),
        adminAPI.getTestimonials(),
        adminAPI.getMedia().catch(() => ({ data: { files: [] } }))
      ]);

      setStats({
        contacts: contactsRes.data?.submissions?.length || 0,
        services: servicesRes.data?.services?.length || 0,
        projects: projectsRes.data?.projects?.length || 0,
        testimonials: testimonialsRes.data?.testimonials?.length || 0,
        media: mediaRes.data?.files?.length || 0,
        views: Math.floor(Math.random() * 1000) + 500 // Simulated for now
      });

      // Generate recent activity (simulated for now)
      setRecentActivity([
        {
          id: 1,
          type: 'contact',
          message: 'Nouvelle demande de contact reçue',
          time: '2 minutes ago',
          icon: <Mail className="w-4 h-4" />
        },
        {
          id: 2,
          type: 'project',
          message: 'Projet "Rénovation Villa" mis à jour',
          time: '1 heure ago',
          icon: <FileText className="w-4 h-4" />
        },
        {
          id: 3,
          type: 'media',
          message: '3 nouvelles images ajoutées',
          time: '3 heures ago',
          icon: <Image className="w-4 h-4" />
        },
        {
          id: 4,
          type: 'testimonial',
          message: 'Nouveau témoignage publié',
          time: '1 jour ago',
          icon: <Star className="w-4 h-4" />
        }
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, change }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {change && (
            <div className="flex items-center mt-2">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-sm text-green-600">+{change}% ce mois</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Vue d'ensemble de votre site consultanttravaux.fr</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
        <StatCard
          title="Demandes de contact"
          value={stats.contacts}
          icon={<Mail className="w-6 h-6 text-white" />}
          color="bg-blue-500"
          change={12}
        />
        <StatCard
          title="Services"
          value={stats.services}
          icon={<FileText className="w-6 h-6 text-white" />}
          color="bg-green-500"
        />
        <StatCard
          title="Projets"
          value={stats.projects}
          icon={<BarChart3 className="w-6 h-6 text-white" />}
          color="bg-purple-500"
        />
        <StatCard
          title="Témoignages"
          value={stats.testimonials}
          icon={<Star className="w-6 h-6 text-white" />}
          color="bg-yellow-500"
        />
        <StatCard
          title="Fichiers médias"
          value={stats.media}
          icon={<Image className="w-6 h-6 text-white" />}
          color="bg-indigo-500"
        />
        <StatCard
          title="Vues du site"
          value={stats.views}
          icon={<Globe className="w-6 h-6 text-white" />}
          color="bg-red-500"
          change={8}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Activité récente</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3">
                  <div className="p-2 bg-gray-100 rounded-full">
                    {activity.icon}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity.message}</p>
                    <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                Voir toute l'activité →
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Actions rapides</h2>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg text-sm font-medium transition-colors">
                Ajouter un nouveau projet
              </button>
              <button className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg text-sm font-medium transition-colors">
                Créer un nouveau service
              </button>
              <button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg text-sm font-medium transition-colors">
                Uploader des images
              </button>
              <button className="w-full bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg text-sm font-medium transition-colors">
                Modifier le contenu
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Site Status */}
      <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">État du site</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Globe className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Site en ligne</h3>
              <p className="text-sm text-gray-600">Votre site est accessible et fonctionne correctement</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Calendar className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Dernière mise à jour</h3>
              <p className="text-sm text-gray-600">Aujourd'hui à {new Date().toLocaleTimeString()}</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <Users className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Visiteurs aujourd'hui</h3>
              <p className="text-sm text-gray-600">{Math.floor(Math.random() * 50) + 20} visiteurs uniques</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;